import { FormEvent, useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import {
  Badge,
  Button,
  Empty,
  ErrorText,
  Note,
  PageStack,
  PrimaryText,
  SearchInput,
  SecondaryText,
  Select,
  Table,
  TableScroll,
  Td,
  Th,
  Toolbar,
  Tr,
} from "../ui";
import { PageProps, formatDate } from "./types";
import {
  addPartnerStaffApi,
  getPartnerStaffApi,
  removePartnerStaffApi,
  revokePartnerInviteApi,
  updatePartnerStaffRoleApi,
} from "../../../api/apiEndpoints";

type Role = "owner" | "manager" | "frontDesk";

const ROLES: { value: Role; label: string; description: string }[] = [
  { value: "owner", label: "Owner", description: "Everything, including managing staff" },
  { value: "manager", label: "Manager", description: "Everything except managing staff" },
  { value: "frontDesk", label: "Front desk", description: "Members and check-ins; no earnings" },
];
const roleLabel = (r: string) => ROLES.find((x) => x.value === r)?.label ?? r;

interface StaffMember {
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  permissionRole: Role;
  isPrimaryOwner: boolean;
  createdAt: string;
}

interface Invite {
  inviteId: string;
  email: string | null;
  permissionRole: Role;
  expiresAt: string;
}

interface StaffResponse {
  staff: StaffMember[];
  pendingInvites: Invite[];
  invited?: boolean;
}

const errorMessage = (err: any) =>
  err?.response?.data?.message || err?.response?.data?.error || "Something went wrong. Please try again.";

const StaffPage = ({ businessId }: PageProps) => {
  const [data, setData] = useState<StaffResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("frontDesk");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!businessId) return;
    setLoadError(null);
    getPartnerStaffApi(businessId)
      .then((res) => setData(res.data))
      .catch((err) => setLoadError(errorMessage(err)));
  }, [businessId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const run = async (action: () => Promise<{ data: StaffResponse }>, successNotice?: string) => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await action();
      setData(res.data);
      if (successNotice) setNotice(successNotice);
      return res.data;
    } catch (err) {
      setActionError(errorMessage(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!businessId || !email.trim()) return;
    const result = await run(() => addPartnerStaffApi(businessId, email.trim(), role));
    if (result) {
      setNotice(
        result.invited
          ? `Invite emailed to ${email.trim()}. It expires in 10 days.`
          : `${email.trim()} now has access.`
      );
      setEmail("");
    }
  };

  const handleRemove = (s: StaffMember) => {
    const name = `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || s.email;
    if (!businessId || !window.confirm(`Remove ${name}'s access to this studio?`)) return;
    run(() => removePartnerStaffApi(businessId, s.userId), `${name} no longer has access.`);
  };

  const handleRevoke = (i: Invite) => {
    if (!businessId || !window.confirm(`Cancel the invite to ${i.email}?`)) return;
    run(() => revokePartnerInviteApi(businessId, i.inviteId), "Invite cancelled.");
  };

  return (
    <PageStack>
      <Panel
        title="Invite Staff"
        subtitle="Give someone their own login to this studio's dashboard"
      >
        <form onSubmit={handleInvite}>
          <Toolbar style={{ justifyContent: "flex-start", marginBottom: 0 }}>
            <SearchInput
              type="email"
              placeholder="name@yourstudio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email address"
              required
            />
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)} aria-label="Role">
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
            <Button type="submit" $variant="primary" disabled={busy || !email.trim()}>
              <UserPlus size={15} />
              Send invite
            </Button>
          </Toolbar>
        </form>
        <Note>
          {ROLES.map((r) => (
            <span key={r.value} style={{ display: "block" }}>
              <strong>{r.label}:</strong> {r.description}
            </span>
          ))}
        </Note>
        {notice && <Note style={{ color: "#1f6f4a" }}>{notice}</Note>}
        {actionError && <ErrorText>{actionError}</ErrorText>}
      </Panel>

      <Panel title="People With Access">
        {loadError ? (
          <ErrorText>{loadError}</ErrorText>
        ) : !data ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} $h="40px" $mb="8px" />)
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Person</Th>
                  <Th>Role</Th>
                  <Th>Added</Th>
                  <Th $align="right" />
                </tr>
              </thead>
              <tbody>
                {data.staff.map((s) => (
                  <Tr key={s.userId}>
                    <Td>
                      <PrimaryText>
                        {`${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || s.email}
                      </PrimaryText>
                      <SecondaryText>{s.email}</SecondaryText>
                    </Td>
                    <Td data-label="Role">
                      {s.isPrimaryOwner ? (
                        <Badge $tone="purple">Primary owner</Badge>
                      ) : (
                        <Select
                          value={s.permissionRole}
                          disabled={busy}
                          aria-label={`Role for ${s.email}`}
                          onChange={(e) =>
                            businessId &&
                            run(
                              () => updatePartnerStaffRoleApi(businessId, s.userId, e.target.value),
                              "Role updated."
                            )
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Td>
                    <Td data-label="Added" $muted>{formatDate(s.createdAt)}</Td>
                    <Td data-label="" $align="right">
                      {!s.isPrimaryOwner && (
                        <Button $variant="danger" disabled={busy} onClick={() => handleRemove(s)}>
                          Remove
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Panel>

      {data && data.pendingInvites.length > 0 && (
        <Panel title="Pending Invites">
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Expires</Th>
                  <Th $align="right" />
                </tr>
              </thead>
              <tbody>
                {data.pendingInvites.map((i) => (
                  <Tr key={i.inviteId}>
                    <Td>{i.email}</Td>
                    <Td data-label="Role">
                      <Badge $tone="gray">{roleLabel(i.permissionRole)}</Badge>
                    </Td>
                    <Td data-label="Expires" $muted>{formatDate(i.expiresAt)}</Td>
                    <Td data-label="" $align="right">
                      <Button disabled={busy} onClick={() => handleRevoke(i)}>
                        Cancel invite
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Panel>
      )}
      {data && data.staff.length === 0 && <Empty>No one else has access yet.</Empty>}
    </PageStack>
  );
};

export default StaffPage;
